import Title from "common/Typography/Title";
import Flex from "common/Boxes/Flex";
import ExchangeCard from "features/portfolio/CreateAccount/ExchangeCard";
import { typography } from "@shared/common/Themes/typography";
import styled, { useTheme } from "styled-components";
import ChangeAccountTypeMessage from "features/configuration/accounts/CreateAccount/ChangeAccountTypeMessage";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Text from "common/Typography/Text";
import useSelectExchange from "@shared/features/portfolio/createAccount/useSelectExchange";
import { hexToRGB } from "@shared/utils/misc";
import { MOBILE_SEARCH } from "@shared/common/constants";
import useMedia from "utils/hooks/useMedia";
import { BOTTOM_NAVIGATOR, INTERCOM_TOTAL_SIZE } from "common/constants";
import { useBottomNavigator } from "utils/hooks/misc";
import InviteExternalUser from "features/configuration/InviteExternalUsers/InviteExternalUser.tsx";
import { useContext } from "react";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";

interface SelectExchangeProps {
  isOnboarding?: boolean;
  onSelectExchange?: (primaryExchange: string) => void;
}

const SelectExchange = ({
  isOnboarding,
  onSelectExchange,
}: SelectExchangeProps) => {
  const theme = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { exchangesLoading, filteredExchanges, filtersData } =
    useSelectExchange();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isExternalConnectAccount } =
    useContext(ExternalConnectAccountContext) || {};

  const filters = (
    <Flex style={{ columnGap: 8, marginTop: isMobile ? 12 : 0 }}>
      <Text style={{ marginRight: 4 }}>Filter by:</Text>
      {filtersData.filtersTags.map((filterTag) => {
        return (
          <FilterButton
            key={filterTag}
            onClick={() => filtersData.onClick(filterTag)}
            $selected={filtersData.getIsSelected(filterTag)}
            $isMobile={isMobile}
          >
            {filterTag}
          </FilterButton>
        );
      })}
    </Flex>
  );

  const mobilePadding =
    isBottomNavigator && !isExternalConnectAccount
      ? { padding: `0 ${PADDING_SIDES}px` }
      : undefined;

  return (
    <Wrapper
      $isOnboarding={isOnboarding}
      $isBottomNavigator={isBottomNavigator}
    >
      <Flex
        style={{
          marginBottom: isMobile ? 24 : 28,
          justifyContent: isOnboarding ? "flex-end" : "space-between",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "flex-start" : "center",
          ...mobilePadding,
        }}
      >
        {!isOnboarding && (
          <Title level={3} $weight={typography.fontWeights.SEMI_BOLD}>
            Select Exchange
          </Title>
        )}
        {filters}
      </Flex>
      <LoadingSpinnerOverlay
        isLoading={exchangesLoading}
        backgroundColor={theme.colors.layoutBgColor}
        spinnerMarginTop={150}
      >
        <CardsWrapper
          $isOnboarding={isOnboarding}
          $isMobile={isMobile}
          $isBottomNavigator={isBottomNavigator}
          style={isExternalConnectAccount ? { padding: 0 } : undefined}
        >
          {filteredExchanges.map(([primaryExchange, tags]) => (
            <ExchangeCard
              key={primaryExchange}
              primaryExchange={primaryExchange}
              tags={tags}
              onSelectExchange={onSelectExchange}
            />
          ))}
        </CardsWrapper>
      </LoadingSpinnerOverlay>
      {!isExternalConnectAccount && (
        <>
          <Flex $justifyContent="center" style={mobilePadding}>
            <ChangeAccountTypeMessage
              style={{
                marginTop: isBottomNavigator
                  ? PADDING_SIDES
                  : isOnboarding
                  ? 32
                  : 24,
                maxWidth: isOnboarding ? 620 : "100%",
                alignSelf: "center",
              }}
            />
          </Flex>
          <InviteExternalUser style={{ marginTop: 12 }} />
        </>
      )}
    </Wrapper>
  );
};

export default SelectExchange;

const PADDING_SIDES = 16;

const Wrapper = styled.div<{
  $isOnboarding?: boolean;
  $isBottomNavigator?: boolean;
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: ${(p) => (p.$isOnboarding ? 0 : INTERCOM_TOTAL_SIZE)}px;
  ${(p) =>
    p.$isBottomNavigator &&
    `
      flex: 1;
      overflow: hidden;
      padding-bottom: 0;
    `};
`;
const CardsWrapper = styled.div<{
  $isOnboarding?: boolean;
  $isMobile?: boolean;
  $isBottomNavigator?: boolean;
}>`
  display: grid;
  grid-gap: 12px;
  grid-template-columns: ${(p) =>
    p.$isMobile ? "1fr" : p.$isOnboarding ? "1fr 1fr 1fr" : "1fr 1fr"};
  min-height: 75px;
  ${(p) =>
    p.$isBottomNavigator &&
    `
      overflow: auto;
      padding: 0 ${PADDING_SIDES}px;
      flex: 1;
      display: flex;
      flex-direction: column;
    `};
`;

const FilterButton = styled.div<{
  $selected: boolean;
  $isMobile: boolean;
}>`
  color: ${(p) => p.theme.colors.primaryGrey};
  border: ${(p) => p.theme.borders.light};
  border-color: ${(p) => p.theme.colors.lightBorderGrey};
  border-radius: 4px;
  padding: 4px 12px;
  cursor: pointer;
  ${(p) =>
    p.$selected &&
    `
      color: ${p.theme.colors.primaryBlue};
      background-color: ${p.theme.colors.lightBlue};
      border: none;
    `};

  ${(p) =>
    p.$isMobile &&
    `
    font-size: ${p.theme.typography.fontSizes.S};
  `};

  @media (min-width: ${BOTTOM_NAVIGATOR}) {
    transition: border-color 0.4s ease-in-out;
    &:hover {
      border-color: ${(p) => p.theme.colors.primaryBlue};
    }
    ${(p) =>
      p.$selected &&
      `
      &:hover {
         background-color: ${hexToRGB(p.theme.colors.lightBlue, 0.7)};
      }
    `};
  }
`;
